import { useQuery } from '@tanstack/react-query';
import { client as sdkClient } from '@blazeteams/sdk/client';

/**
 * Tenant feature flags (módulos on/off) — `GET /me/modules`.
 *
 * Contract (blazeteams-backend/docs/rgdimax/02-CONTRACTS.md §1.1): the endpoint
 * returns the standard envelope with a FLAT map of module key → boolean:
 *
 *   { "success": true, "data": { "inbox": true, "kanban": false, … } }
 *
 * Beyond the fixed 14-module catalog, tenant-contracted connector flags ride on
 * the same map (e.g. `blazeconnector`, `send_to_bts` for the BTS payment-proof
 * pipeline). We normalize defensively because the shape has drifted in sibling
 * endpoints before — accepted inputs:
 *   - flat map            { blazeconnector: true, … }
 *   - nested map          { modules: { blazeconnector: true, … } }
 *   - array of ON keys    ['blazeconnector', 'send_to_bts']
 *   - any of the above inside the `{ success, data }` envelope
 *
 * Fail-mode note: the contract doc recommends fail-SAFE (assume enabled) for
 * the core nav catalog so a failed fetch never blanks the sidebar. The helpers
 * here are used to gate OPTIONAL, opt-in connector UI (BTS menu items, billing
 * panel) — for those we deliberately fail CLOSED (absent/loading → hidden), so
 * a tenant that never contracted BlazeConnector can't see half-wired buttons.
 */
export type ModulesMap = Record<string, boolean>;

/**
 * Feature keys that only count as ON when their parent module is also ON.
 * `send_to_bts` is a capability OF the BlazeConnector integration — with the
 * connector off there is nothing to send to, so the flag is considered off
 * regardless of its own value.
 */
const MODULE_DEPENDENCIES: Record<string, string> = {
  send_to_bts: 'blazeconnector',
};

/** Extract a normalized key→boolean map from whatever shape the backend sent. */
export function normalizeModules(payload: unknown): ModulesMap | undefined {
  if (payload == null) return undefined;

  // Array of enabled keys → every listed key is ON.
  if (Array.isArray(payload)) {
    const map: ModulesMap = {};
    for (const key of payload) {
      if (typeof key === 'string' && key) map[key] = true;
    }
    return map;
  }

  if (typeof payload !== 'object') return undefined;
  const obj = payload as Record<string, unknown>;

  // Envelope / nesting: `{ data: … }` or `{ modules: … }` (possibly both).
  if ('data' in obj && obj.data != null && typeof obj.data === 'object') {
    return normalizeModules(obj.data);
  }
  if ('modules' in obj && obj.modules != null && typeof obj.modules === 'object') {
    return normalizeModules(obj.modules);
  }

  // Flat map — keep only boolean values (skip envelope leftovers like
  // `success: true`, which is a boolean but not a module… except it IS a
  // boolean. Guard by dropping the known envelope keys instead).
  const map: ModulesMap = {};
  let sawBoolean = false;
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'success' || key === 'error' || key === 'meta') continue;
    if (typeof value === 'boolean') {
      map[key] = value;
      sawBoolean = true;
    }
  }
  return sawBoolean ? map : undefined;
}

/**
 * Pure gate: is module `key` enabled in `modulesData`?
 *
 * - Tolerates every shape `normalizeModules` accepts (raw envelope, flat map,
 *   nested `{modules}`, array of keys) — callers can pass the query result or
 *   the raw response interchangeably.
 * - Fail-closed: `undefined`/malformed data or an unknown key → `false`.
 * - Applies the dependency rule: `send_to_bts` is only ON when
 *   `blazeconnector` is also ON.
 */
export function isModuleEnabled(modulesData: unknown, key: string): boolean {
  if (!key) return false;
  const map = normalizeModules(modulesData);
  if (!map) return false;
  if (map[key] !== true) return false;
  const parent = MODULE_DEPENDENCIES[key];
  if (parent && map[parent] !== true) return false;
  return true;
}

export const moduleQueries = {
  /**
   * The tenant's module flags. Long staleTime (flags change via a super_admin
   * panel, not mid-session) and prefetch-friendly — `AuthInitializer` can
   * `queryClient.prefetchQuery(moduleQueries.myModules())` on login.
   * Endpoint isn't in the generated SDK → shared client (same pattern as
   * `inboxQueries.myPermissions`).
   */
  myModules: () => ({
    queryKey: ['me', 'modules'] as const,
    queryFn: async (): Promise<ModulesMap> => {
      const { data, error } = await sdkClient.get<unknown>({ url: '/me/modules' });
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Failed to load modules',
        );
      }
      return normalizeModules(data) ?? {};
    },
    staleTime: 10 * 60_000,
  }),
};

/**
 * Is a tenant module/feature flag enabled? `false` while the query is loading
 * or errored — fail-closed on purpose: this hook gates OPTIONAL connector UI
 * (e.g. the "Enviar a BTS" menu item), and flashing then removing an action
 * the tenant never contracted is worse than it appearing a beat later.
 */
export function useModuleEnabled(key: string): boolean {
  const { data } = useQuery(moduleQueries.myModules());
  return data ? isModuleEnabled(data, key) : false;
}
